import type { ReactNode } from "react";
import { Link } from "react-router";
import type { FinancialPlanData } from "@/generated/core/v1/financial_plan";
import type { Family } from "@/generated/core/v1/family";
import { formatDate } from "@/plan-logic/lib/dates";
import { money } from "@/plan-logic/lib/format";
import {
  type EntityIndex,
  type FlatAction,
  unconfirmedFhsaTransfers,
} from "@/plan-logic/plan-view";
import { fhsaWarning } from "@/plan-logic/plan-view/actionText";
import { FILL_MISSING_URL, missingText, missingValues } from "./missingValues";
import { FIX_FIRST_TIME_BUYER_URL } from "./useFamilySave";

/**
 * "Check your data" cards: the pinned first rows of Home's action list and Sharpen's list, one way
 * of saying "something in your data needs attention". In order: each unconfirmed FHSA move, each
 * unconfirmed empty FHSA, then "Fill in missing values".
 */
export function DataCheckRow({
  testId,
  title,
  children,
  to,
  cta,
  warn,
}: {
  testId: string;
  title: string;
  children: ReactNode;
  to: string;
  cta: string;
  warn?: boolean;
}) {
  return (
    <Link className="actrow flat missrow" to={to} data-testid={testId}>
      <span className="ck2 miss" aria-hidden="true">
        !
      </span>
      <span style={{ flex: 1 }}>
        <b>{title}</b>
        <span className={`wy${warn ? " warn" : ""}`}>{children}</span>
      </span>
      <span className="wt go">{cta} &rarr;</span>
    </Link>
  );
}

/**
 * An empty FHSA the engine closes because its holder isn't a first-time home buyer, which the
 * household hasn't confirmed (the setup policy's `close_fhsas_of_non_first_time_buyers`: an FHSA
 * whose `account_value > 0` is moved to the RRSP instead, which `unconfirmedFhsaTransfers` finds in
 * the plan). Selected from the family document: an unset balance counts as 0, as the engine's
 * defaults pass fills it.
 */
export type EmptyFhsa = { personUuid: string; fhsaUuid: string; who: string; name: string };

export function emptyUnconfirmedFhsas(
  family: Family | undefined | null,
  plan?: FinancialPlanData | null,
): EmptyFhsa[] {
  if (!family) return [];
  // A plan from an older document may still move it: that FHSA gets the move's card instead.
  const moved = new Set(plan ? unconfirmedFhsaTransfers(plan).map(fhsaSource) : []);
  return family.persons.flatMap((p) =>
    p.isFirstTimeHomeBuyer
      ? []
      : p.fhsaAccounts
          .filter(
            (a) => !a.transferToRrspConfirmed && (a.currentValue ?? 0) <= 0 && !moved.has(a.uuid),
          )
          .map((a) => ({
            personUuid: p.uuid,
            fhsaUuid: a.uuid,
            who: p.givenName,
            name: a.name.trim(),
          })),
  );
}

function fhsaSource(f: FlatAction): string {
  const t = f.action.actionType;
  return t.oneofKind === "transferFhsaToRrspAction"
    ? t.transferFhsaToRrspAction.sourceFhsaUuid
    : "";
}

const isYou = (who: string | undefined) => !who || who === "You";

/** The warning on an unconfirmed empty FHSA (`fhsaWarning`, adapted: nothing moves). */
export function emptyFhsaWarning(e: EmptyFhsa): string {
  const named = e.name ? ` (${e.name})` : "";
  if (isYou(e.who))
    return `Your data says you aren’t a first-time home buyer, so Abi closes your empty FHSA${named}. If it’s a mistake, update your data.`;
  return `Your data says ${e.who} isn’t a first-time home buyer, so Abi closes ${e.who}’s empty FHSA${named}. If it’s a mistake, update your data.`;
}

/** The pinned "check your data" rows. Nothing when nothing needs attention. */
export function DataChecks({
  family,
  plan,
  index,
}: {
  family: Family;
  plan?: FinancialPlanData | null;
  index: EntityIndex;
}) {
  const moves = plan ? unconfirmedFhsaTransfers(plan) : [];
  const empty = emptyUnconfirmedFhsas(family, plan);
  const missing = missingValues(family);
  const whose = (memberUuid: string) => {
    const who = index.get(memberUuid)?.name;
    return isYou(who) ? "your" : `${who}’s`;
  };
  return (
    <>
      {moves.map((f) => (
        <DataCheckRow
          key={`move:${f.action.id}`}
          testId={`fhsa-move-${f.action.id}`}
          title={`Check ${whose(f.memberUuid)} FHSA before you move it`}
          to={FIX_FIRST_TIME_BUYER_URL}
          cta="Update your data"
          warn
        >
          Warning! {fhsaWarning(f, index)} Abi&rsquo;s plan moves {money(f.amount)} on{" "}
          {formatDate(f.date)}.
        </DataCheckRow>
      ))}
      {empty.map((e) => (
        <DataCheckRow
          key={`empty:${e.fhsaUuid}`}
          testId={`fhsa-empty-${e.fhsaUuid}`}
          title={`Check ${isYou(e.who) ? "your" : `${e.who}’s`} empty FHSA`}
          to={FIX_FIRST_TIME_BUYER_URL}
          cta="Update your data"
          warn
        >
          Warning! {emptyFhsaWarning(e)}
        </DataCheckRow>
      ))}
      {missing.length > 0 && (
        <DataCheckRow
          testId="missing-values"
          title="Fill in missing values"
          to={FILL_MISSING_URL}
          cta={`Fill ${missing.length === 1 ? "it" : "them"} in`}
        >
          {missingText(missing)}
        </DataCheckRow>
      )}
    </>
  );
}
